/**
 * API 和静态资源配置
 * 
 * 开发环境：前端直连后端（CORS），由 React 决定 API 和资源地址
 * 测试/生产环境：使用相对路径，由 Nginx 反向代理决定实际地址
 * 
 * 重要原则：
 * - React 只负责请求 URL，不负责"连接"后端
 * - 开发环境：React 决定 API 和资源地址（通过环境变量）
 * - 生产环境：Nginx 决定实际地址（通过反向代理）
 * - API 和静态资源使用不同的基础 URL，语义清晰
 */

// 从环境变量获取 API 基础 URL
// 开发环境：REACT_APP_API_BASE_URL=http://localhost:3001/api（前端直连后端，需要 CORS，包含 /api 前缀）
// 生产环境：REACT_APP_API_BASE_URL=/api（相对路径，由 Nginx 代理）
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  // 如果环境变量未设置，默认使用相对路径（生产环境通过 nginx 代理）
  if (!envUrl || envUrl.trim() === '') {
    // 开发环境警告：如果缺少 .env.development，会使用生产环境的默认值，可能导致无法连接后端
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  REACT_APP_API_BASE_URL 未设置，使用默认值 /api');
      console.warn('⚠️  开发环境建议创建 .env.development 并设置：');
      console.warn('   REACT_APP_API_BASE_URL=http://localhost:3001/api');
      console.warn('   REACT_APP_ASSET_BASE_URL=http://localhost:3001');
    }
    return '/api';
  }
  
  // 返回环境变量配置的 URL（开发环境为完整 URL，生产环境为相对路径）
  return envUrl.trim();
};

// 从环境变量获取静态资源基础 URL
// 开发环境：REACT_APP_ASSET_BASE_URL=http://localhost:3001（前端直连后端，用于 videos/... 等静态资源）
// 生产环境：REACT_APP_ASSET_BASE_URL=（空，使用相对路径，由 Nginx 代理）
const getAssetBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_ASSET_BASE_URL;
  
  // 如果环境变量未设置，默认使用相对路径（生产环境通过 nginx 代理）
  if (!envUrl || envUrl.trim() === '') {
    // 开发环境警告：如果缺少 .env.development，会使用生产环境的默认值
    // 注意：这里只在 API_BASE_URL 未设置时显示一次警告，避免重复提示
    // （警告已在 getApiBaseUrl 中显示）
    return '';
  }
  
  // 返回环境变量配置的 URL（开发环境为完整 URL，生产环境为空字符串使用相对路径）
  return envUrl.trim();
};

// 从环境变量获取上传专用 API 基础 URL
// 开发环境：REACT_APP_UPLOAD_API_BASE_URL=http://localhost:3001/api（可选，仅用于覆盖上传请求）
// 生产环境：REACT_APP_UPLOAD_API_BASE_URL=https://api.example.com/api（可选）
const getUploadApiBaseUrl = (apiBaseUrl: string): string => {
  const envUrl = process.env.REACT_APP_UPLOAD_API_BASE_URL;

  // 未设置时回退到通用 API_BASE_URL，保持现有行为
  if (!envUrl || envUrl.trim() === '') {
    return apiBaseUrl;
  }

  return envUrl.trim();
};

export const API_BASE_URL = getApiBaseUrl();
export const ASSET_BASE_URL = getAssetBaseUrl();
export const UPLOAD_API_BASE_URL = getUploadApiBaseUrl(API_BASE_URL);

// 开发环境调试：输出配置值
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API_BASE_URL:', API_BASE_URL);
  console.log('🔧 ASSET_BASE_URL:', ASSET_BASE_URL || '(相对路径)');
  console.log('🔧 UPLOAD_API_BASE_URL:', UPLOAD_API_BASE_URL);
  console.log('🔧 REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
  console.log('🔧 REACT_APP_ASSET_BASE_URL:', process.env.REACT_APP_ASSET_BASE_URL);
  console.log('🔧 REACT_APP_UPLOAD_API_BASE_URL:', process.env.REACT_APP_UPLOAD_API_BASE_URL);
}

